import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { enrichRecommendations } from '@theme/frest-recommendation-ui';

class ProductRecommendations extends Component {
  #intersectionObserver = new IntersectionObserver((entries, observer) => {
    if (!entries[0]?.isIntersecting) return;
    observer.disconnect();
    this.#loadRecommendations();
  }, { rootMargin: '0px 0px 400px 0px' });

  #mutationObserver = new MutationObserver(() => {
    this.dataset.recommendationsPerformed = 'false';
    this.classList.remove('hidden');
    this.#loadRecommendations();
  });

  #cachedRecommendations = new Map();
  #activeFetch = null;
  #requestVersion = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, {
      attributes: true,
      attributeFilter: ['data-product-id', 'data-section-id', 'data-intent', 'data-url', 'data-rec-current', 'data-rec-context', 'data-feed-url'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#requestVersion += 1;
    this.#activeFetch?.abort();
  }

  async #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent, hydrationKey } = this.dataset;
    if (!productId || !sectionId || recommendationsPerformed === 'true') return;

    const version = ++this.#requestVersion;
    this.#activeFetch?.abort();
    const controller = new AbortController();
    this.#activeFetch = controller;

    try {
      if (intent === 'related') {
        await enrichRecommendations(this, { signal: controller.signal, isCurrent: () => version === this.#requestVersion });
        if (!controller.signal.aborted && version === this.#requestVersion && this.dataset.productId === productId) {
          this.dataset.recommendationsPerformed = 'true';
          delete this.dataset.error;
        }
        return;
      }
      const url = new URL(this.dataset.url, window.location.origin);
      // Shopify accepts a maximum of 10 native results. Collection fallback may show more.
      const requestedLimit = Number(url.searchParams.get('limit')) || 10;
      url.searchParams.set('limit', String(Math.min(10, Math.max(1, requestedLimit))));
      url.searchParams.set('product_id', productId);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('intent', intent || 'related');
      const cacheKey = `${this.dataset.recContext}|${url.href}`;
      let html = this.#cachedRecommendations.get(cacheKey);
      if (!html) {
        const response = await fetch(url.href, { signal: controller.signal });
        if (!response.ok) throw new Error(`Server returned ${response.status}`);
        html = await response.text();
      }
      if (controller.signal.aborted || version !== this.#requestVersion || !this.isConnected) return;

      // A successful HTTP request can still contain missing or unrelated section HTML.
      const document = new DOMParser().parseFromString(html, 'text/html');
      const responseSection = document.getElementById(`shopify-section-${sectionId}`);
      const responseComponent = Array.from(responseSection?.querySelectorAll('product-recommendations') || [])
        .find((element) => element.dataset.hydrationKey === hydrationKey);
      if (!responseComponent || responseComponent.dataset.productId !== productId) {
        throw new Error('Recommendation response did not match this artwork');
      }
      this.#cachedRecommendations.set(cacheKey, html);
      // Do not replace cards while a visitor is keyboard-browsing the current fallback.
      if (this.contains(window.document.activeElement) && this.querySelector('.related-products__item')) {
        this.dataset.recommendationsPerformed = 'true';
        return;
      }
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      if (controller.signal.aborted || version !== this.#requestVersion || this.dataset.productId !== productId) return;
      for (const grid of this.querySelectorAll('related-products-progressive')) grid.refresh?.();
      this.dataset.recommendationsPerformed = 'true';
      delete this.dataset.error;
    } catch (error) {
      if (error.name === 'AbortError' || version !== this.#requestVersion) return;
      if (!window.Shopify?.designMode) console.error('Product recommendations error:', error.message);
      // Keep useful, server-rendered subject recommendations on API/network failure.
      if (intent !== 'related' && !this.querySelector('.related-products__item')) this.classList.add('hidden');
      this.dataset.recommendationsPerformed = 'true';
      this.dataset.error = 'Error loading product recommendations';
    } finally {
      if (this.#activeFetch === controller) this.#activeFetch = null;
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
